import { useCallback, useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"

import { getIcon } from "utils/icon"
import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { Carousel, CarouselItem } from "components/Carousel"
import { Head } from "components/Head"
import { EmptyData } from "components/EmptyData"
import { CalendarStarIcon } from "components/icons"

import { VenueSelector } from "components/VenueSelector"
import { SearchToggleButton } from "components/domains/search"
import { MotionDiv, showContent } from "components/motions"
import { LanguageButton } from "extensions/iconsiam/components/language/LanguageButton"
import { useEvent } from "extensions/iconsiam/hooks/event/useEvent"
import { VerticalEventCard } from "extensions/iconsiam/components/domains/events"

export const EventsKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { openSearch } = useAppUI()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    isMultiVenueProject,
    venueFilter,
    venues,
    locationVenueId,
    events,
    changeVenueFilter,
  } = useEvent()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.events) || <CalendarStarIcon />,
    [theme]
  )
  const { styleOverrides } = theme.components?.[`EventPage`] || {}
  const { eventCarouselOption } =
    theme.components[`EventPage`]?.defaultProps || {}
  const {
    root,
    carouselContainer,
    carousel,
    emptyDataWrapper,
    navigationWrapper,
    venueSelector,
    toolbarContainer,
    actionButton,
    actionContainer,
    searchBarContainer,
  } = styleOverrides
  const slidePerPage = 3
  const carouselType = events && events.length < slidePerPage ? "slide" : "loop"
  const autoplay = events && events.length > 3
  const focus = events.length < 3 ? "center" : eventCarouselOption.focus

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  const direction = theme.direction

  return (
    <>
      <Head title={t("Events & Activities")} />
      <Layout>
        <Box sx={root}>
          <MotionDiv
            animate="animate"
            initial="initial"
            variants={showContent}
            sx={carouselContainer}
          >
            {!events || events.length <= 0 ? (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t(`No Current Events`)}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            ) : (
              <Carousel
                onClick={handleClickSlide}
                options={{
                  type: carouselType,
                  perPage: slidePerPage,
                  pagination: true,
                  trimSpace: false,
                  autoplay,
                  gap: "0",
                  direction: direction,
                  ...eventCarouselOption,
                  focus,
                }}
                sx={carousel}
              >
                {events.map((event) => {
                  const sponsoredId = event?.properties?.sponsoredId
                  return (
                    <CarouselItem
                      link={`/events/${event.id}`}
                      key={`${sponsoredId || ""}${event.id}`}
                    >
                      <VerticalEventCard dense={true} event={event} />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            )}
          </MotionDiv>

          <Box key="app-navigation" sx={navigationWrapper}>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={changeVenueFilter}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageButton sx={actionButton} />
                {additionalMenu}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
